import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Avatar, 
  Rating, 
  Button,
  Container,
  Stack
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

/**
 * TestimonialCard - Professional testimonial component with semantic markup
 * Maximizes credibility with proper schema.org structure and trust signals
 */
function TestimonialCard({
  quote = "Finally, a UK calculator that shows me exactly where my tax goes. The visual bands make it much clearer than spreadsheets.",
  name = "Sarah M.",
  role = "Financial Planner",
  avatarSrc,
  companyLogoSrc,
  ratingValue,
  ratingCount,
  cta = { label: "See my take-home pay", to: "/salary-calculator" },
}) {
  return (
    <Box component="section" sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 10 } }}>
      <Container maxWidth="md" sx={{ textAlign: 'center', px:3 }}>
        {/* Section Label */}
        <Typography 
          variant="overline" 
          sx={{ 
            color: 'text.secondary', 
            letterSpacing: '0.06em', 
            fontWeight: 600,
            textTransform: 'uppercase',
            display: 'block',
            mb: 3
          }}
        >
          What users say
        </Typography>

        {/* Testimonial Card */}
        <Paper 
          elevation={1} 
          sx={{ 
            mt: 2, 
            borderRadius: { xs: 2, md: 3 },
            p: { xs: 3, sm: 4, md: 5 }, 
            position: 'relative',
            maxWidth: 840,
            mx: 'auto'
          }}
        >
          {/* Decorative Quote Mark */}
          <Box 
            aria-hidden 
            sx={{ 
              position: 'absolute', 
              top: 14, 
              left: 14, 
              fontSize: 22, 
              color: 'grey.300',
              fontFamily: 'serif'
            }}
          >
            &ldquo;
          </Box>

          {/* Semantic Figure Structure */}
          <Box component="figure" itemScope itemType="https://schema.org/Review" sx={{ m: 0 }}>
            {/* Hidden Schema.org metadata */}
            <meta itemProp="itemReviewed" itemScope itemType="https://schema.org/Product" />
            <meta itemProp="name" content="SalaryTools" />

            {/* Quote Content */}
            <Typography
              component="blockquote"
              itemProp="reviewBody"
              sx={{ 
                m: 0, 
                fontSize: { xs: '1.125rem', md: '1.375rem' }, 
                lineHeight: { xs: 1.5, md: 1.6 }, 
                fontStyle: 'normal', 
                color: 'text.primary',
                maxWidth: '70ch',
                mx: 'auto',
                mb: 3
              }}
            >
              Finally, a UK calculator that shows me{' '}
              <Box component="strong" sx={{ fontWeight: 700 }}>exactly where my tax goes</Box>.
              {' '}The <Box component="strong" sx={{ fontWeight: 700 }}>visual bands</Box> make it much clearer than spreadsheets.
            </Typography>

            {/* Rating Section (conditional) */}
            {ratingValue && (
              <Stack spacing={1} alignItems="center" sx={{ mt: 2.5 }}>
                <Rating
                  value={ratingValue}
                  precision={0.1}
                  readOnly
                  size="small"
                  sx={{ color: 'warning.main' }}
                  aria-label={`Rated ${ratingValue} out of 5 stars`}
                />
                {/* Show user count text only if count is >= 50 */}
                {ratingCount && Number(ratingCount) >= 50 && (
                  <Typography variant="body2" color="text.secondary">
                    Rated {ratingValue}/5 by {Number(ratingCount).toLocaleString()}+ users
                  </Typography>
                )}
                {/* centred divider */}
                <Box sx={{ width: 120, height: 1, bgcolor: 'divider', mx: 'auto', my: 1.25 }} />
              </Stack>
            )}
            
            {/* Fallback divider when rating is hidden */}
            {!ratingValue && (
              <Box sx={{ width: 120, height: 1, bgcolor: 'divider', mx: 'auto', my: 2.5 }} />
            )}

            {/* Attribution */}
            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
              {avatarSrc && (
                <Avatar 
                  src={avatarSrc} 
                  alt={`${name}`} 
                  sx={{ width: 28, height: 28 }} 
                />
              )}
              {companyLogoSrc && (
                <Box 
                  component="img" 
                  src={companyLogoSrc} 
                  alt="" 
                  aria-hidden 
                  width={28} 
                  sx={{ opacity: 0.6, filter: 'grayscale(100%)' }} 
                />
              )}
              <Box>
                <Typography 
                  component="figcaption" 
                  variant="subtitle2" 
                  itemProp="author" 
                  sx={{ fontWeight: 700 }}
                >
                  — {name}
                </Typography>
                {role && (
                  <Typography variant="caption" color="text.secondary">
                    {role}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
        </Paper>

        {/* CTA Button */}
        {cta?.label && (
          <Button 
            component={Link} 
            to={cta.to} 
            variant="contained" 
            size="large" 
            endIcon={<ArrowForwardIcon />} 
            sx={{ 
              mt: { xs: 3.5, md: 4.5 },
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-1px)'
              }
            }}
          >
            {cta.label}
          </Button>
        )}
      </Container>
    </Box>
  );
}

/**
 * HeroTestimonial - Compact one-liner variant for hero sections
 * Minimal design with maximum impact
 */
function HeroTestimonial({
  quote = "Finally, a UK calculator that shows me exactly where my tax goes.",
  name = "Sarah M.",
  role = "Financial Planner",
  ratingValue
}) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        maxWidth: 600,
        mx: 'auto',
        px: { xs: 3, sm: 2 }
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 2,
          borderRadius: 3,
          bgcolor: alpha('#000', 0.03),
          border: '1px solid',
          borderColor: alpha('#000', 0.08),
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}
      >
        {ratingValue && (
          <Rating
            value={ratingValue}
            precision={0.1}
            readOnly
            size="small"
            sx={{ color: 'warning.main' }}
          />
        )}
        
        <Typography
          variant="body2"
          sx={{
            fontStyle: 'italic',
            color: 'text.secondary',
            fontSize: '0.9rem',
            maxWidth: '40ch'
          }}
        >
          "{quote}"
        </Typography>
        
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
            whiteSpace: 'nowrap'
          }}
        >
          — {name}{role && `, ${role}`}
        </Typography>
      </Box>
    </Box>
  );
}

export { TestimonialCard, HeroTestimonial };
export default TestimonialCard;